import { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Outlet, useSearchParams } from 'react-router';
import { AUTH_PAGES } from '@pmai/contracts';
import { Card, ChoiceButtons, TextField, type TextFieldProps } from '@pmai/ui';
import { type Language, LANGUAGES, setLanguage } from '../../i18n';
import { useDocumentTitle } from '../../shared/use-document-title';

/** Frame of the public pages (login, sign-up, e-mail links). */
export function AuthLayout() {
  const { t, i18n } = useTranslation();
  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-6 px-4 py-10">
      <div className="text-3xl font-bold tracking-tight text-brand-700">{t('app.name')}</div>
      <div className="w-full max-w-md">
        <Outlet />
      </div>
      <ChoiceButtons
        label={t('app.language')}
        value={i18n.language as Language}
        options={LANGUAGES.map((l) => ({ value: l, label: l.toUpperCase() }))}
        onChange={setLanguage}
      />
    </div>
  );
}

/** Card with title and optional footer links — every auth page is one of these. */
export function AuthCard({
  title,
  children,
  footer,
}: {
  title: string;
  children: ReactNode;
  footer?: ReactNode;
}) {
  useDocumentTitle(title);
  return (
    <Card>
      <h1 className="mb-5 text-xl font-semibold">{title}</h1>
      {children}
      {footer && (
        <div className="mt-6 flex flex-col gap-1.5 text-center text-sm text-ink-muted">{footer}</div>
      )}
    </Card>
  );
}

export function BackToLogin() {
  const { t } = useTranslation();
  return (
    <Link to={AUTH_PAGES.login} className="text-brand-700 hover:underline">
      {t('auth.backToLogin')}
    </Link>
  );
}

/** The one-time token of e-mail links (`?token=…`). */
export function useTokenParam(): string | null {
  const [params] = useSearchParams();
  return params.get('token');
}

/** A new password with the policy hint — sign-up, invitation and password reset. */
export function NewPasswordField({ label, ...field }: Omit<TextFieldProps, 'label'> & { label?: string }) {
  const { t } = useTranslation();
  return (
    <TextField
      label={label ?? t('auth.password')}
      hint={t('auth.register.passwordHint')}
      type="password"
      autoComplete="new-password"
      required
      {...field}
    />
  );
}
